/** biome-ignore-all lint/suspicious/noExplicitAny: TODO: fix */
import { generateUUIDv4 } from '@/utils/number';
import type {
  Package,
  PurchaseOrderItem,
  PurchaseOrderListItem,
} from '@/views/screens/PurchaseOrderScreens/PurchaseOrder/types';
import type { ShipmentDetailedItemVendorApi } from '@/views/screens/Shipment/types';
import { ALL_LOCATIONS_OPTION } from '@/views/screens/VendorAddress/constants';
import type { VendorAddress, VendorAddressLocationType } from '@/views/screens/VendorAddress/types';

import { convertDateESTToUTCToUserLocal, truncateTimeZoneDate } from '../../utilz/dates';
import { DEFAULT_STORE_OBJECT } from '../constants';
import type { DefaultStoreObject } from '../types';
import POVTypes from './POVTypes';

export interface VendorSearchFilterRequestParams {
  vendorAddressKey: number;
  vendorKey: number;
  country: string;
  name: string;
  addressConcat: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  contactName: string;
  businessPhone: string;
  phoneExt: string;
  phoneCountry: string;
  daysOfWeek: string;
  hoursOfOperation: string;
  appointmentType: string;
  email: string;
  pageIndex: number;
  pageSize: number;
  orderByColumn: string | null;
  orderBySort: string;
  locationType: VendorAddressLocationType | typeof ALL_LOCATIONS_OPTION.value;
  portRecKey: number;
  portType?: string;
}

export interface PurchaseOrderVendorState extends Record<string, any> {
  // Purchase Order
  purchaseOrderListVendor: DefaultStoreObject<PurchaseOrderListItem[]>;
  packageModalVisible?: {
    showModal: boolean;
    packageKey: string;
    mainKey?: string;
    shippedId?: any;
    totalshipped?: any;
    isPackLoading?: boolean;
    data?: any;
  };
  isCheckItemLevelConsignee: 'Y' | 'N' | '';
  checkedAllPOV: boolean;
  purchaseOrderVendorTotalCount?: { totalCount: number };
  packageList: DefaultStoreObject<Package[]>;
  PODetails: DefaultStoreObject<PurchaseOrderItem[]>;
  regularOrderInfo: DefaultStoreObject<{ status: number; message: string }[]>;

  // Vendor Address
  vendorSearchList: DefaultStoreObject<VendorAddress[]> & {
    totalCount: number;
  };
  vendorSearchListFilter: VendorSearchFilterRequestParams;
  deleteVendorAddress: DefaultStoreObject<null>;
  saveVendorAddress: DefaultStoreObject<null>;

  // Shipment
  vendorShipmentDetailList: DefaultStoreObject<ShipmentDetailedItemVendorApi>;
}

const vendorPayloadParams: VendorSearchFilterRequestParams = {
  address: '',
  addressConcat: '',
  appointmentType: '',
  businessPhone: '',
  city: '',
  contactName: '',
  country: '',
  daysOfWeek: '',
  email: '',
  hoursOfOperation: '',
  locationType: ALL_LOCATIONS_OPTION.value,
  name: '',
  orderByColumn: '',
  orderBySort: '',
  pageIndex: 0,
  pageSize: 10,
  phoneCountry: '',
  phoneExt: '',
  portRecKey: 0,
  state: '',
  vendorAddressKey: 0,
  vendorKey: 0,
  zip: '',
};

const initialState: PurchaseOrderVendorState = {
  cancelShipment: [],
  checkedAllDraft: false,
  checkedAllPOV: false,
  createShipment: { ...DEFAULT_STORE_OBJECT, data: [] },
  customerRouterSetting: { ...DEFAULT_STORE_OBJECT, data: [] },
  deleteVendorAddress: {
    ...DEFAULT_STORE_OBJECT,
  },

  getBarCodeData: { data: [] },
  isCheckItemLevelConsignee: '',
  PODetails: { ...DEFAULT_STORE_OBJECT, data: [] },
  packageList: { ...DEFAULT_STORE_OBJECT, data: [] },

  // Create Shipment
  packageLoadTypeOptions: [],
  packageModalVisible: undefined,
  // Purchase Order
  purchaseOrderListVendor: { ...DEFAULT_STORE_OBJECT, data: [] },
  purchaseOrderVendorTotalCount: undefined,
  regularOrderInfo: { ...DEFAULT_STORE_OBJECT, data: [] },
  removedShipmentItemDetail: [],
  savedShipmentData: { data: [] },
  saveShipment: { ...DEFAULT_STORE_OBJECT, data: [] },
  saveVendorAddress: {
    ...DEFAULT_STORE_OBJECT,
  },
  selectedConsignee: [],
  selectedShipmentItemDetail: [],
  selectedShipmentSummary: [],
  selectedShipper: [],
  vendorConsigneeList: { data: [] },
  vendorSearchList: {
    ...DEFAULT_STORE_OBJECT,
    data: [],
    totalCount: 0,
  },

  // Vendor Address
  vendorSearchListFilter: {
    ...vendorPayloadParams,
  },
  vendorShipmentDetailList: { ...DEFAULT_STORE_OBJECT },
  vendorShipmentItemDetailList: { data: [] },
  vendorShipmentLadingList: { data: [] },
  vendorShipmentSummaryList: { data: [] },
  vendorShipperList: { data: [] },
};

// biome-ignore lint/suspicious/noExplicitAny: TODO: fix
const POVReducer = (state = initialState, { type, payload }: any): PurchaseOrderVendorState => {
  switch (type) {
    case POVTypes.PACKAGE_LOAD_TYPE_DATA: {
      return {
        ...state,
        packageLoadTypeOptions: payload,
      };
    }

    case POVTypes.GET_CUSTOMER_ROUTING_SETTING: {
      return {
        ...state,
        customerRouterSetting: {
          ...DEFAULT_STORE_OBJECT,
          data: [],
          progressing: true,
        },
      };
    }

    case POVTypes.GET_CUSTOMER_ROUTING_SETTING_SUCCESS: {
      return {
        ...state,
        customerRouterSetting: {
          ...DEFAULT_STORE_OBJECT,
          data: payload?.data || [],
          success: true,
        },
      };
    }

    case POVTypes.GET_CUSTOMER_ROUTING_SETTING_FAIL: {
      return {
        ...state,
        customerRouterSetting: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
          data: [],
        },
      };
    }

    case POVTypes.GET_VENDOR_PURCHASE_ORDER_LIST_PROGRESS: {
      return {
        ...state,
        purchaseOrderListVendor: {
          ...DEFAULT_STORE_OBJECT,
          data: state.purchaseOrderListVendor.data,
          progressing: true,
        },
      };
    }

    case POVTypes.GET_VENDOR_PURCHASE_ORDER_LIST_ERROR: {
      return {
        ...state,
        purchaseOrderListVendor: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
          data: [...(state.purchaseOrderListVendor.data || [])],
        },
      };
    }

    case POVTypes.SET_SELECT_ALL_CHECK_POV: {
      if (payload?.data) {
        state.purchaseOrderListVendor.data?.forEach((element) => {
          if (element.uniqueId === payload.data.uniqueId) {
            element.isChecked = payload?.checked || false;
          }
        });
      } else {
        state.purchaseOrderListVendor.data?.forEach((element) => {
          element.isChecked = payload?.checked || false;
        });
      }
      const findCheckItem = state.purchaseOrderListVendor.data?.filter((item) => item.isChecked === true);

      let temp = false;
      if (
        Boolean(state.purchaseOrderListVendor.data?.length) &&
        findCheckItem?.length === state.purchaseOrderListVendor.data?.length
      ) {
        temp = true;
      }

      return {
        ...state,
        checkedAllPOV: temp,
        purchaseOrderListVendor: {
          ...DEFAULT_STORE_OBJECT,
          data: state.purchaseOrderListVendor.data,
          success: true,
        },
      };
    }

    case POVTypes.GET_VENDOR_PURCHASE_ORDER_LIST_SUCCESS: {
      // @ts-expect-error
      payload.data.forEach((element) => {
        element.uniqueId = generateUUIDv4();
        element.isChecked = state.checkedAllPOV || false;
        element.orderShipDate = convertDateESTToUTCToUserLocal(element.orderShipDate);
        element.orderDeliveryDate = convertDateESTToUTCToUserLocal(element.orderDeliveryDate);
      });
      let listData = [...(state.purchaseOrderListVendor.data || []), ...payload.data];
      // @ts-expect-error
      payload.selectedPurchaseOrders.forEach((ele) => {
        ele.isChecked = true;
      });
      listData = [...payload.selectedPurchaseOrders, ...listData];
      const uniqueList = listData.filter((obj, index, self) => {
        return index === self.findIndex((o) => o.rowID === obj.rowID);
      });
      return {
        ...state,
        isCheckItemLevelConsignee: payload.isCheckItemLevelConsignee,
        purchaseOrderListVendor: {
          ...DEFAULT_STORE_OBJECT,
          data: uniqueList,
          success: true,
        },
      };
    }

    case POVTypes.SET_VENDOR_PURCHASE_ORDER_LIST: {
      return {
        ...state,
        purchaseOrderListVendor: {
          ...DEFAULT_STORE_OBJECT,
          ...payload,
        },
      };
    }

    case POVTypes.GET_PO_INFO_PROGRESS: {
      return {
        ...state,
        PODetails: {
          ...DEFAULT_STORE_OBJECT,
          data: state.PODetails.data,
          progressing: true,
        },
      };
    }

    case POVTypes.GET_PO_INFO_ERROR: {
      return {
        ...state,
        PODetails: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
          data: state.PODetails.data,
          progressing: false,
        },
      };
    }

    case POVTypes.GET_PO_INFO_SUCCESS: {
      return {
        ...state,
        PODetails: {
          ...DEFAULT_STORE_OBJECT,
          // @ts-expect-error
          data: payload.data.map((element) => ({
            ...element,
            expShipDate: truncateTimeZoneDate(convertDateESTToUTCToUserLocal(element.expShipDate)),
            orderDeliveryDate: truncateTimeZoneDate(convertDateESTToUTCToUserLocal(element.orderDeliveryDate)),
            orderShipDate: truncateTimeZoneDate(convertDateESTToUTCToUserLocal(element.orderShipDate)),
          })),
          progressing: false,
          success: true,
        },
      };
    }

    case POVTypes.SET_PO_INFO_DEFAULT: {
      return {
        ...state,
        PODetails: {
          ...DEFAULT_STORE_OBJECT,
          ...payload,
        },
      };
    }

    case POVTypes.GET_PACKAGE_INFO_SUCCESS: {
      return {
        ...state,
        packageList: {
          ...DEFAULT_STORE_OBJECT,
          data: payload.data,
          success: true,
        },
      };
    }

    case POVTypes.REGULAR_ORDER_DATA_RESET: {
      return {
        ...state,
        regularOrderInfo: {
          ...state.regularOrderInfo,
          data: [],
          success: false,
        },
      };
    }

    case POVTypes.SAVE_REGULAR_ORDER_DATA_PROGRESS: {
      return {
        ...state,
        regularOrderInfo: {
          ...DEFAULT_STORE_OBJECT,
          data: [],
          progressing: true,
        },
      };
    }

    case POVTypes.SAVE_REGULAR_ORDER_DATA_ERROR: {
      return {
        ...state,
        regularOrderInfo: {
          ...DEFAULT_STORE_OBJECT,
          data: state.regularOrderInfo.data,
          error: false,
        },
      };
    }

    case POVTypes.SAVE_REGULAR_ORDER_DATA_SUCCESS: {
      return {
        ...state,
        regularOrderInfo: {
          ...DEFAULT_STORE_OBJECT,
          data: payload.data,
          success: true,
        },
      };
    }

    case POVTypes.SET_PACKAGE_MODAL_TOGGLE: {
      return {
        ...state,
        packageModalVisible: payload,
      };
    }

    case POVTypes.SAVE_PACKAGE_DETAILS_PROGRESS: {
      return {
        ...state,
        packageList: {
          ...DEFAULT_STORE_OBJECT,
          data: [],
          progressing: true,
        },
      };
    }

    case POVTypes.SAVE_PACKAGE_DETAILS_ERROR: {
      return {
        ...state,
        packageList: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
          data: [...(state.packageList.data || []), ...payload.data],
        },
      };
    }

    case POVTypes.SAVE_PACKAGE_DETAILS_SUCCESS: {
      return {
        ...state,
        packageList: {
          ...DEFAULT_STORE_OBJECT,
          data: [...(state.packageList.data || []), ...payload.data],
          success: true,
        },
      };
    }

    case POVTypes.SAVE_SHIPMENT_DETAILS_PROGRESS: {
      return {
        ...state,
        cancelShipment: [],
        createShipment: { ...DEFAULT_STORE_OBJECT, data: [] },
        saveShipment: {
          ...DEFAULT_STORE_OBJECT,
          data: [],
          progressing: true,
        },
      };
    }

    case POVTypes.SAVE_SHIPMENT_DETAILS_ERROR: {
      return {
        ...state,
        cancelShipment: [],
        createShipment: { ...DEFAULT_STORE_OBJECT, data: [] },
        saveShipment: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
          data: [...state.saveShipment.data, ...payload.data],
        },
      };
    }

    case POVTypes.SAVE_SHIPMENT_DETAILS_SUCCESS: {
      return {
        ...state,
        cancelShipment: [],
        createShipment: { ...DEFAULT_STORE_OBJECT, data: [] },
        saveShipment: {
          ...DEFAULT_STORE_OBJECT,
          data: [...state.saveShipment.data, ...payload.data],
          success: true,
        },
      };
    }
    case POVTypes.GET_VENDOR_SHIPPER_LIST_PROGRESS: {
      return {
        ...state,
        saveShipment: { ...DEFAULT_STORE_OBJECT, data: [] },
        vendorShipperList: {
          data: state.vendorShipperList.data,
          progressing: true,
        },
      };
    }

    case POVTypes.GET_VENDOR_SHIPPER_LIST_ERROR: {
      return {
        ...state,
        vendorShipperList: {
          error: true,
          ...payload,
          data: [...state.vendorShipperList.data],
        },
      };
    }

    case POVTypes.GET_VENDOR_SHIPPER_LIST_SUCCESS: {
      return {
        ...state,
        vendorShipperList: {
          data: payload.data?.totalData,
          success: true,
        },
      };
    }

    case POVTypes.GET_VENDOR_CONSIGNEE_LIST_PROGRESS: {
      return {
        ...state,
        vendorConsigneeList: {
          data: state.vendorConsigneeList.data,
          progressing: true,
        },
      };
    }

    case POVTypes.GET_VENDOR_CONSIGNEE_LIST_ERROR: {
      return {
        ...state,
        vendorConsigneeList: {
          error: true,
          ...payload,
          data: [...state.vendorConsigneeList.data],
        },
      };
    }

    case POVTypes.GET_VENDOR_CONSIGNEE_LIST_SUCCESS: {
      return {
        ...state,
        vendorConsigneeList: {
          data: payload.data,
          success: true,
        },
      };
    }

    case POVTypes.GET_VENDOR_SHIPMENT_DETAIL_LIST_PROGRESS: {
      return {
        ...state,
        vendorShipmentDetailList: {
          ...state.vendorShipmentDetailList,
          data: null,
          progressing: true,
        },
      };
    }

    case POVTypes.GET_VENDOR_SHIPMENT_DETAIL_LIST_ERROR: {
      return {
        ...state,
        vendorShipmentDetailList: {
          error: true,
          ...payload,
          data: state.vendorShipmentDetailList.data,
        },
      };
    }

    case POVTypes.GET_VENDOR_SHIPMENT_DETAIL_LIST_SUCCESS: {
      return {
        ...state,
        vendorShipmentDetailList: {
          ...DEFAULT_STORE_OBJECT,
          data: payload.data,
          success: true,
        },
      };
    }

    case POVTypes.GET_VENDOR_SHIPMENT_ITEM_DETAIL_LIST_PROGRESS: {
      return {
        ...state,
        vendorShipmentItemDetailList: {
          data: state.vendorShipmentItemDetailList.data,
          progressing: true,
        },
      };
    }

    case POVTypes.GET_VENDOR_SHIPMENT_ITEM_DETAIL_LIST_ERROR: {
      return {
        ...state,
        vendorShipmentItemDetailList: {
          error: true,
          ...payload,
          data: state.vendorShipmentItemDetailList.data,
        },
      };
    }

    case POVTypes.GET_VENDOR_SHIPMENT_ITEM_DETAIL_LIST_SUCCESS: {
      return {
        ...state,
        vendorShipmentItemDetailList: {
          data: Array.isArray(payload.data)
            ? // @ts-ignore
              payload.data.map((element) => ({
                ...element,
                expShipDate: convertDateESTToUTCToUserLocal(element.expShipDate),
                orderDeliveryDate: convertDateESTToUTCToUserLocal(element.orderDeliveryDate),
                orderShipDate: convertDateESTToUTCToUserLocal(element.orderShipDate),
              }))
            : payload.data,
          success: true,
        },
      };
    }

    case POVTypes.GET_BAR_CODE: {
      return {
        ...state,
        getBarCodeData: {
          data: state.getBarCodeData.data,
          progressing: true,
        },
      };
    }

    case POVTypes.GET_BAR_CODE_SUCCESS: {
      return {
        ...state,
        getBarCodeData: {
          data: payload.data,
          success: true,
        },
      };
    }

    case POVTypes.GET_VENDOR_SHIPMENT_SUMMARY_LIST_PROGRESS: {
      return {
        ...state,
        vendorShipmentSummaryList: {
          data: state.vendorShipmentSummaryList.data,
          progressing: true,
        },
      };
    }

    case POVTypes.GET_VENDOR_SHIPMENT_SUMMARY_LIST_ERROR: {
      return {
        ...state,
        vendorShipmentSummaryList: {
          error: true,
          ...payload,
          data: [...state.vendorShipmentSummaryList.data],
        },
      };
    }

    case POVTypes.GET_VENDOR_SHIPMENT_SUMMARY_LIST_SUCCESS: {
      if (payload?.isUpdate === true) {
        return {
          ...state,
          vendorShipmentSummaryList: {
            data: payload.data,
            success: true,
          },
        };
      }
      // @ts-expect-error
      payload.data.forEach((item) => {
        item.renderKey = generateUUIDv4();
        item.dimensionsUom = item.unitMeasure;
      });
      return {
        ...state,
        vendorShipmentSummaryList: {
          data: payload.data,
          success: true,
        },
      };
    }

    case POVTypes.REMOVE_VENDOR_SUMMARY_LIST: {
      return {
        ...state,
        vendorShipmentSummaryList: {
          ...state.vendorShipmentSummaryList,
          data: payload,
        },
      };
    }

    case POVTypes.GET_VENDOR_SHIPMENT_LADING_LIST_PROGRESS: {
      return {
        ...state,
        vendorShipmentLadingList: {
          data: state.vendorShipmentLadingList.data,
          progressing: true,
        },
      };
    }

    case POVTypes.GET_VENDOR_SHIPMENT_LADING_LIST_ERROR: {
      return {
        ...state,
        vendorShipmentLadingList: {
          error: true,
          ...payload,
          data: [...state.vendorShipmentLadingList.data],
        },
      };
    }

    case POVTypes.GET_VENDOR_SHIPMENT_LADING_LIST_SUCCESS: {
      return {
        ...state,
        vendorShipmentLadingList: {
          data: payload.data,
          success: true,
        },
      };
    }

    case POVTypes.SET_SELECTED_SHIPPER: {
      return {
        ...state,
        selectedShipper: payload,
      };
    }

    case POVTypes.SET_SELECTED_CONSIGNEE: {
      return {
        ...state,
        selectedConsignee: payload,
      };
    }

    case POVTypes.CREATE_SHIPMENT_PROGRESS: {
      return {
        ...state,
        createShipment: {
          ...DEFAULT_STORE_OBJECT,
          data: [],
          progressing: true,
        },
      };
    }

    case POVTypes.CREATE_SHIPMENT_ERROR: {
      return {
        ...state,
        createShipment: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
          data: [...state.createShipment.data, ...payload.data],
        },
      };
    }

    case POVTypes.CREATE_SHIPMENT_SUCCESS: {
      return {
        ...state,
        createShipment: {
          ...DEFAULT_STORE_OBJECT,
          data: payload.data,
          success: true,
        },
      };
    }

    case POVTypes.CREATE_SHIPMENT_RESET: {
      return {
        ...state,
        createShipment: {
          ...DEFAULT_STORE_OBJECT,
          data: [],
        },
      };
    }

    case POVTypes.GET_ROUTING_GUIDE: {
      return {
        ...state,
        getRoutingGuide: {
          ...DEFAULT_STORE_OBJECT,
          data: [],
          progressing: true,
        },
      };
    }

    case POVTypes.SET_PAYLOAD_SHIP_DATA: {
      return {
        ...state,
        payloadShipData: payload,
      };
    }

    case POVTypes.GET_ROUTING_GUIDE_SUCCESS: {
      return {
        ...state,
        getRoutingGuide: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
          data: [...state.getRoutingGuide.data, ...payload.data],
        },
      };
    }

    case POVTypes.GET_ROUTING_GUIDE_ERROR: {
      return {
        ...state,
        getRoutingGuide: {
          ...DEFAULT_STORE_OBJECT,
          data: payload.data,
          success: true,
        },
      };
    }

    case POVTypes.SET_PURCHASE_ORDER_TOTAL_COUNT: {
      return {
        ...state,
        purchaseOrderVendorTotalCount: { ...payload },
      };
    }

    case POVTypes.SET_SELECTED_SHIPMENT_ITEM_DETAIL: {
      // @ts-expect-error
      payload.forEach((element) => {
        element.createdOn = convertDateESTToUTCToUserLocal(element.createdOn);
        element.catchDate = convertDateESTToUTCToUserLocal(element.catchDate);
      });
      return {
        ...state,
        selectedShipmentItemDetail: payload,
      };
    }

    case POVTypes.SET_REMOVED_SHIPMENT_ITEM_DETAIL: {
      return {
        ...state,
        removedShipmentItemDetail: {
          ...DEFAULT_STORE_OBJECT,
          ...payload,
        },
      };
    }

    case POVTypes.SET_SELECTED_SHIPMENT_SUMMARY: {
      return {
        ...state,
        selectedShipmentSummary: payload,
      };
    }

    case POVTypes.SAVE_VENDOR_ADDRESS_PROGRESS: {
      return {
        ...state,
        saveVendorAddress: {
          ...DEFAULT_STORE_OBJECT,
          data: null,
          progressing: true,
        },
      };
    }

    case POVTypes.SAVE_VENDOR_ADDRESS_ERROR: {
      return {
        ...state,
        saveVendorAddress: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }

    case POVTypes.SAVE_VENDOR_ADDRESS_SUCCESS: {
      return {
        ...state,
        saveVendorAddress: {
          ...DEFAULT_STORE_OBJECT,
          success: true,
        },
      };
    }

    case POVTypes.SAVE_VENDOR_ADDRESS_RESET: {
      return {
        ...state,
        saveVendorAddress: {
          ...DEFAULT_STORE_OBJECT,
          success: false,
        },
      };
    }

    case POVTypes.SET_SAVE_SHIPMENT_DATA: {
      return {
        ...state,
        savedShipmentData: payload,
      };
    }

    case POVTypes.CANCEL_SHIPMENT_PROGRESS: {
      return {
        ...state,
        cancelShipment: {
          ...DEFAULT_STORE_OBJECT,
          data: [],
          progressing: true,
        },
      };
    }

    case POVTypes.CANCEL_SHIPMENT_ERROR: {
      return {
        ...state,
        cancelShipment: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
          data: payload.data,
        },
      };
    }

    case POVTypes.CANCEL_SHIPMENT_SUCCESS: {
      return {
        ...state,
        cancelShipment: {
          ...DEFAULT_STORE_OBJECT,
          data: payload.data,
          success: true,
        },
      };
    }

    case POVTypes.GET_VENDOR_SEARCH: {
      return {
        ...state,
        vendorSearchList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.vendorSearchList,
          progressing: true,
        },
      };
    }

    case POVTypes.GET_VENDOR_SEARCH_SUCCESS: {
      // @ts-expect-error
      payload?.data?.totalData.forEach((element) => {
        // biome-ignore lint/suspicious/noDoubleEquals: TODO: fix
        if (typeof element?.id == 'undefined') {
          element.id = generateUUIDv4();
        }
        // biome-ignore lint/suspicious/noDoubleEquals: TODO: fix
        if (typeof element.isEditMode == 'undefined') {
          element.isEditMode = false;
        }
        // biome-ignore lint/suspicious/noDoubleEquals: TODO: fix
        if (typeof element.isChecked == 'undefined') {
          element.isChecked = false;
        }
      });
      return {
        ...state,
        vendorSearchList: {
          ...DEFAULT_STORE_OBJECT,
          data: [...(state.vendorSearchList.data || []), ...(payload.data?.totalData ?? [])],
          progressing: false,
          success: true,
          totalCount: payload.data?.count?.totalCount,
        },
      };
    }

    case POVTypes.GET_VENDOR_SEARCH_FAIL: {
      return {
        ...state,
        vendorSearchList: {
          ...DEFAULT_STORE_OBJECT,
          ...state.vendorSearchList,
          data: state.vendorSearchList.data,
          error: true,
          progressing: false,
        },
      };
    }

    case POVTypes.GET_VENDOR_SEARCH_CLEAR: {
      return {
        ...state,
        vendorSearchList: {
          ...DEFAULT_STORE_OBJECT,
          data: [],
          totalCount: 0,
        },
      };
    }
    case POVTypes.GET_VENDOR_SEARCH_UPDATE: {
      if (payload.actionType === 'update') {
        return {
          ...state,
          vendorSearchList: {
            ...state.vendorSearchList,
            data: [...payload.data],
          },
        };
      }
      return {
        ...state,
        vendorSearchList: {
          ...DEFAULT_STORE_OBJECT,
          data: [...payload.data],
          success: true,
          totalCount:
            payload.actionType === 'delete'
              ? state.vendorSearchList.totalCount - 1
              : state.vendorSearchList.totalCount + 1,
        },
      };
    }
    case POVTypes.GET_VENDOR_SEARCH_FILTER: {
      return {
        ...state,
        vendorSearchListFilter: {
          ...state.vendorSearchListFilter,
          ...payload,
        },
      };
    }

    case POVTypes.ADD_VENDOR_ADDRESS_RECORD: {
      const newRecord: VendorAddress = {
        address: '',
        addressConcat: '',
        appointmentType: null,
        businessPhone: '',
        city: '',
        contactName: '',
        country: '',
        countryName: '',
        daysOfWeek: '',
        email: '',
        hoursOfOperation: '',
        isEditMode: true,
        isNew: true,
        locationType: 'S',
        name: '',
        phoneExt: '',
        portName: null,
        portRecKey: null,
        portType: null,
        shipperReferenceNo: '',
        state: '',
        stateDesc: '',
        vendorAddressKey: 0,
        vendorKey: 0,
        zip: '',
      };
      return {
        ...state,
        vendorSearchList: {
          ...state.vendorSearchList,
          data: [newRecord, ...(state.vendorSearchList.data || [])],
        },
      };
    }

    case POVTypes.DELETE_VENDOR_ADDRESS: {
      return {
        ...state,
        deleteVendorAddress: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
        },
      };
    }

    case POVTypes.DELETE_VENDOR_ADDRESS_SUCCESS: {
      return {
        ...state,
        deleteVendorAddress: {
          ...DEFAULT_STORE_OBJECT,
          progressing: false,
          success: true,
        },
      };
    }

    case POVTypes.DELETE_VENDOR_ADDRESS_FAIL: {
      return {
        ...state,
        deleteVendorAddress: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          progressing: false,
        },
      };
    }

    case POVTypes.DELETE_VENDOR_ADDRESS_RESET: {
      return {
        ...state,
        deleteVendorAddress: {
          ...DEFAULT_STORE_OBJECT,
          success: false,
        },
      };
    }

    default:
      return state;
  }
};

// biome-ignore lint/style/noDefaultExport: TODO: fix
export default POVReducer;
